<template>
    <div class="menu-editor">
        <div class="row items-center q-mb-md q-gutter-sm">
            <q-btn icon="add" label="Nuevo item" color="primary" dense flat @click="addRootItem" />
            <q-space />
            <div class="row items-center q-gutter-md q-mx-md">
                <q-badge v-if="hasCreated" color="green-2" text-color="green-9" label="Nuevo" />
                <q-badge v-if="hasUpdated" color="blue-2" text-color="blue-9" label="Editado" />
                <q-badge v-if="hasDeleted" color="red-2" text-color="red-9" label="Eliminado" />
            </div>
            <q-btn v-if="menuChanged" icon="save" label="Guardar Menú" color="primary" dense unelevated @click="save" :loading="saving" />
        </div>

        <!-- ── Permissions panel ── -->
        <div class="row items-center q-mb-md q-gutter-sm q-pa-sm bg-grey-2 rounded-borders">
            <q-select
                v-model="permTarget"
                dense outlined
                label="Usuario o Rol"
                :options="permOptions"
                option-label="label"
                option-value="value"
                emit-value
                map-options
                clearable
                use-input
                input-debounce="200"
                @filter="filterPermOptions"
                style="min-width: 280px;"
                @update:model-value="loadAccess"
                :loading="permOptionsLoading"
                :input-attrs="{ autocomplete: 'off' }"
            >
                <template v-slot:no-option>
                    <q-item><q-item-section class="text-grey">Sin resultados</q-item-section></q-item>
                </template>
            </q-select>
            <q-space />
            <q-btn
                v-if="permDirty"
                icon="save"
                label="Guardar Permisos"
                color="secondary"
                dense unelevated
                @click="saveAccess"
                :loading="permSaving"
            />
        </div>

        <q-scroll-area style="height: 50vh;">
            <div v-if="loading" class="row items-center justify-center" style="height: 200px;">
                <q-spinner />
                <span class="text-subtitle1 q-ml-md">Cargando menú...</span>
            </div>
            <q-tree
                v-else-if="tree != null"
                :nodes="tree"
                node-key="id"
                label-key="name"
                v-model:expanded="expanded"
                no-selection-unset
            >
                <template v-slot:default-header="{ node }">
                    <div
                        class="full-width menu-node q-py-xs"
                        :class="nodeClass(node)"
                        :draggable="node.id != editingId"
                        @dragstart.stop="onDragStart($event, node)"
                        @dragover.prevent.stop="onDragOver($event, node)"
                        @dragleave="onDragLeave($event)"
                        @drop.prevent.stop="onDrop($event, node)"
                    >
                        <div class="row items-center full-width">
                            <q-icon name="drag_indicator" class="cursor-move q-mr-xs text-grey-6" size="18px" />
                            <q-icon :name="defaultIcon(node)" class="q-mr-sm" size="20px" />

                            <template v-if="editingId === node.id">
                                <span class="text-body2 text-weight-medium">{{ node.name }}</span>
                                <q-space />
                                <q-btn icon="check" flat dense color="positive" @click.stop="confirmEdit(node)">
                                    <q-tooltip>Confirmar</q-tooltip>
                                </q-btn>
                                <q-btn icon="close" flat dense color="grey" @click.stop="editingId = null">
                                    <q-tooltip>Cancelar</q-tooltip>
                                </q-btn>
                            </template>
                            <template v-else>
                                <span
                                    class="text-body2 q-mr-sm"
                                    :class="{ 'text-grey-5': isNodeDimmed(node) }"
                                >{{ node.name }}</span>
                                <span
                                    v-if="node.url"
                                    class="text-caption q-mr-xs"
                                    :class="isNodeDimmed(node) ? 'text-grey-4' : 'text-grey-6'"
                                >{{ node.url }}</span>
                                <q-space />

                                <!-- ── Access buttons (perm mode) ── -->
                                <template v-if="permMode && node.obj_type && node.object_real_id">
                                    <div class="row no-wrap q-gutter-xs q-mr-sm" @click.stop>
                                        <template v-if="node.obj_type === 6"> <!-- Process type: only execute access -->
                                            <q-btn
                                                label="E"
                                                :outline="getNodeAccess(node) !== 'e'"
                                                :color="getNodeAccess(node) === 'e' ? 'purple' : 'grey-5'"
                                                :text-color="getNodeAccess(node) === 'e' ? 'white' : 'grey-7'"
                                                dense unelevated size="sm"
                                                style="min-width: 36px; font-weight: 600;"
                                                @click="toggleAccess(node, 'e')"
                                            >
                                                <q-tooltip>Execute</q-tooltip>
                                            </q-btn>
                                        </template>
                                        <template v-else>
                                            <q-btn
                                                label="R"
                                                :outline="getNodeAccess(node) !== 'r'"
                                                :color="getNodeAccess(node) === 'r' ? 'blue' : 'grey-5'"
                                                :text-color="getNodeAccess(node) === 'r' ? 'white' : 'grey-7'"
                                                dense unelevated size="sm"
                                                style="min-width: 36px; font-weight: 600;"
                                                @click="toggleAccess(node, 'r')"
                                            >
                                                <q-tooltip>Read</q-tooltip>
                                            </q-btn>
                                            <q-btn
                                                label="W"
                                                :outline="getNodeAccess(node) !== 'w'"
                                                :color="getNodeAccess(node) === 'w' ? 'orange' : 'grey-5'"
                                                :text-color="getNodeAccess(node) === 'w' ? 'white' : 'grey-7'"
                                                dense unelevated size="sm"
                                                style="min-width: 36px; font-weight: 600;"
                                                @click="toggleAccess(node, 'w')"
                                            >
                                                <q-tooltip>Write</q-tooltip>
                                            </q-btn>
                                        </template>
                                    </div>
                                </template>

                                <q-btn v-if="node.url" :href="node.url" target="_blank" icon="open_in_new" flat dense size="sm" color="grey" @click.stop>
                                    <q-tooltip>Abrir URL</q-tooltip>
                                </q-btn>
                                <q-btn
                                    v-if="node.obj_type && node.object_real_id"
                                    icon="share"
                                    flat dense size="sm"
                                    color="blue"
                                    @click.stop="openShare(node)"
                                >
                                    <q-tooltip>Compartir</q-tooltip>
                                </q-btn>
                                <q-btn
                                    v-if="node.children && node.children.length"
                                    icon="share_location"
                                    flat dense size="sm"
                                    color="deep-purple"
                                    @click.stop="openShareSubtree(node)"
                                >
                                    <q-tooltip>Compartir con todos los hijos</q-tooltip>
                                </q-btn>
                                <span class="text-caption text-grey-5 q-mx-sm" style="min-width: 20px; text-align: center;">{{ node.seq }}</span>
                                <q-btn icon="add" flat dense size="sm" @click.stop="addChild(node)">
                                    <q-tooltip>Añadir hijo</q-tooltip>
                                </q-btn>
                                <q-btn icon="edit" flat dense size="sm" @click.stop="startEdit(node)">
                                    <q-tooltip>Editar</q-tooltip>
                                </q-btn>
                                <q-btn icon="delete" flat dense size="sm" color="red" @click.stop="markDeleted(node)">
                                    <q-tooltip>Eliminar</q-tooltip>
                                </q-btn>
                            </template>
                        </div>

                        <div v-if="editingId === node.id" class="q-mt-sm q-pa-sm bg-grey-1 rounded-borders" @click.stop @keypress.stop>
                            <div class="row q-col-gutter-sm">
                                <div class="col-12 col-sm-6">
                                    <q-input v-model="editForm.name" dense outlined label="Nombre" />
                                </div>
                                <div class="col-12 col-sm-6">
                                    <q-input v-model="editForm.url" dense outlined label="URL" />
                                </div>
                                <div class="col-12 col-sm-6">
                                    <q-input v-model="editForm.icon" dense outlined label="Icono">
                                        <template v-slot:prepend>
                                            <q-icon :name="editForm.icon || 'circle'" />
                                        </template>
                                        <template v-slot:append>
                                            <q-btn flat dense round icon="apps" size="sm">
                                                <q-menu anchor="bottom right" self="top right" :offset="[0, 4]" class="icon-picker-menu">
                                                    <div class="q-pa-sm" style="width: 340px;">
                                                        <IconPicker
                                                            v-model="editForm.icon"
                                                        />
                                                    </div>
                                                </q-menu>
                                            </q-btn>
                                        </template>
                                    </q-input>
                                </div>
                                <div class="col-12 col-sm-6">
                                    <q-select
                                        v-model="editForm.obj_type"
                                        dense outlined
                                        label="Tipo"
                                        :options="objTypeOpts"
                                        emit-value
                                        map-options
                                        clearable
                                    />
                                </div>
                                <div class="col-12 col-sm-6">
                                    <q-input v-model="editForm.object_real_id" dense outlined label="Object ID" />
                                </div>
                                <div class="col-12 col-sm-6">
                                    <q-input v-model="editForm.access_type" dense outlined label="Acceso" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="dropTarget?.id === node.id && dropPos === 'before'" class="drop-bar drop-bar--before" />
                    <div v-if="dropTarget?.id === node.id && dropPos === 'after'" class="drop-bar drop-bar--after" />
                    <div v-if="dropTarget?.id === node.id && dropPos === 'inside'" class="drop-bar drop-bar--inside" />
                </template>
            </q-tree>
            <div v-else class="row items-center justify-center" style="height: 200px;">
                <span class="text-subtitle1 q-mt-md">No se encontraron datos.</span>
            </div>
        </q-scroll-area>

        <q-scroll-area style="height: 20vh;" class="q-mt-md">
            <div class="text-subtitle2 text-red q-mb-xs">Pendientes de eliminar:</div>
            <div v-for="d in deletedNodes" :key="d.id" class="row items-center q-py-xs q-px-sm bg-red-1 rounded-borders q-mb-xs">
                <q-icon :name="d.icon || 'circle'" class="q-mr-sm" />
                <span>{{ d.name }}</span>
                <q-space />
                <q-btn icon="undo" flat dense size="sm" @click="restoreNode(d)">
                    <q-tooltip>Restaurar</q-tooltip>
                </q-btn>
            </div>
        </q-scroll-area>

        <ShareDialog
            ref="shareDialog"
            :object_type="shareObjectType"
            :related_id_int="shareRelatedIdInt"
            :related_id_uuid="shareRelatedIdUuid"
        />
        <ShareDialog
            ref="shareSubtreeDialog"
            :nodes="shareSubtreeNodes"
            :parent_name="shareSubtreeParentName"
        />
    </div>
</template>

<script>
import { Auth } from '@/database'
import { mapGetters, mapActions } from 'vuex'
import ShareDialog from '@/components/Security/ShareDialog.vue'
import IconPicker from '@/components/IconPicker.vue'

let tempId = -1

export default {
    name: 'MenuEditor',
    components: { ShareDialog, IconPicker },
    props: {
        rootId: { type: Number, default: 1000 },
    },
    emits: ['saved'],

    data: () => ({
        api: new Auth(),
        tree: null,
        expanded: [],
        deletedNodes: [],
        saving: false,
        editingId: null,
        editForm: {},
        iconFilter: '',
        iconPagination: { itemsPerPage: 60, page: 0 },
        dragged: null,
        dropTarget: null,
        dropPos: null,
        shareObjectType: null,
        shareRelatedIdInt: null,
        shareRelatedIdUuid: null,
        shareSubtreeNodes: [],
        shareSubtreeParentName: '',
        objTypeOpts: [
            { value: 1, label: 'Dimension', uuid: false },
            { value: 2, label: 'Model', uuid: false },
            { value: 3, label: 'File', uuid: true },
            { value: 4, label: 'Dashboard', uuid: true },
            { value: 5, label: 'Notification', uuid: true },
            { value: 6, label: 'Process', uuid: true },
        ],
        // ── Permissions state ──
        permTarget: null,
        permOptionsAll: [],
        permOptions: [],
        permOptionsLoading: true,
        visibleIds: new Set(),
        accessMap: {},
        accessChanges: {},
        permSaving: false,
        loading: true,
    }),

    async created() {
        await this.load()
        await Promise.all([this.getUsers(), this.getRoles()])
        this.buildPermOptions()
    },

    computed: {
        ...mapGetters(['users', 'roles', 'user']),
        permMode() { return !!this.permTarget },
        permDirty() { return Object.keys(this.accessChanges).length > 0 },
        hasCreated() {
            const check = (nodes) => nodes.some(n => n._created || (n.children && check(n.children)))
            return this.tree && check(this.tree)
        },
        hasUpdated() {
            const check = (nodes) => nodes.some(n => n._updated || (n.children && check(n.children)))
            return this.tree && check(this.tree)
        },
        hasDeleted() {
            return this.deletedNodes.length > 0
        },
        menuChanged() {
            return this.hasCreated || this.hasUpdated || this.hasDeleted
        },

    },

    methods: {
        ...mapActions(['getUsers', 'getRoles']),

        buildPermOptions() {
            this.permOptionsAll = [
                ...(this.users || []).map(u => ({ label: `Usuario: ${u.username}`, value: { type: 'user', id: u.id } })),
                ...(this.roles || []).map(r => ({ label: `Rol: ${r.name}`, value: { type: 'role', id: r.id } })),
            ]
            this.permOptions = this.permOptionsAll
            this.permOptionsLoading = false
        },

        filterPermOptions(val, update) {
            update(() => {
                const q = (val || '').toLowerCase()
                this.permOptions = q
                    ? this.permOptionsAll.filter(o => o.label.toLowerCase().includes(q))
                    : this.permOptionsAll
            })
        },

        async loadAccess(target) {
            this.accessMap = {}
            this.accessChanges = {}
            this.visibleIds = new Set()
            if (!target) return
            try {
                this.loading = true
                const params = target.type === 'user' ? `user_id=${target.id}` : `role_id=${target.id}`
                const res = await this.api.fetch(`/menu/getMenuAccess/?root_id=${this.rootId}&${params}`)
                const data = await res.json()

                this.visibleIds = new Set(data.visible_ids || [])

                for (const row of (data.access || [])) {
                    const key = `${row.obj_type}:${row.object_id}`
                    if (!this.accessMap[key] || row.access === 'w') {
                        this.accessMap[key] = { access: row.access, access_id: row.access_id }
                    }
                }
            } catch (e) { console.error('loadAccess:', e) }
            finally { this.loading = false }
        },

        accessKey(node) {
            return `${node.obj_type}:${node.object_real_id}`
        },

        getNodeAccess(node) {
            const key = this.accessKey(node)
            if (key in this.accessChanges) return this.accessChanges[key]
            return this.accessMap[key]?.access || null
        },

        toggleAccess(node, val) {
            const key = this.accessKey(node)
            const current = this.getNodeAccess(node)
            const newVal = current === val ? null : val
            const original = this.accessMap[key]?.access || null
            if (newVal === original) {
                delete this.accessChanges[key]
            } else {
                this.accessChanges[key] = newVal
            }
        },

        isProcessType(node) {
            return node.obj_type === 6
        },

        isNodeUserVisible(node) {
            if (node.obj_type && node.object_real_id) {
                const key = this.accessKey(node)
                let hasAccess
                if (key in this.accessChanges) {
                    hasAccess = this.accessChanges[key] != null
                } else {
                    hasAccess = this.visibleIds.has(node.id)
                }
                if (!hasAccess) return false
            }
            if (node.url != null) return true
            if (node.children?.some(c => this.isNodeUserVisible(c))) return true
            return false
        },

        isNodeDimmed(node) {
            if (!this.permMode) return false
            return !this.isNodeUserVisible(node)
        },

        async saveAccess() {
            this.permSaving = true
            try {
                const res = await this.api.fetch('/menu/saveMenuAccess/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        target: this.permTarget,
                        changes: this.accessChanges,
                    }),
                })
                const data = await res.json()
                if (!res.ok) throw new Error(data.detail)
                await this.loadAccess(this.permTarget)
            } catch (e) { console.error('saveAccess:', e); alert('Error: ' + e.message) }
            finally { this.permSaving = false }
        },

        // ── Existing methods ──

        async load() {
            try {
                const res = await this.api.fetch(`/menu/getMenuTree/?root_id=${this.rootId}`)
                const { menu } = await res.json()
                this.tree = menu
                this.deletedNodes = []
                this.loading = false
            } catch (e) { console.error('MenuEditor load:', e); this.loading = false }
        },

        expandAll(nodes) {
            for (const n of nodes) {
                if (n.children?.length) { this.expanded.push(n.id); this.expandAll(n.children) }
            }
        },

        defaultIcon(n) {
            let icon = n.icon || ''
            if (icon && icon.startsWith('bi ')) icon = null
            if (icon) return icon
            if (!n.url) return 'library_books'
            const u = n.url || ''
            if (u.includes('/dashboard/')) return 'analytics'
            if (u.includes('/processexecutor/')) return 'terminal'
            if (u.includes('/queryvisualizer/')) return 'manage_search'
            if (u.includes('/filemanager')) return 'file_open'
            if (u.includes('/processmanager')) return 'terminal'
            if (u.includes('/trained_assistant')) return 'chat'
            return 'circle'
        },

        isUuidType(objType) {
            const opt = this.objTypeOpts.find(o => o.value === objType)
            return opt ? opt.uuid : false
        },

        openShare(node) {
            this.shareObjectType = node.obj_type
            if (this.isUuidType(node.obj_type)) {
                this.shareRelatedIdInt = null
                this.shareRelatedIdUuid = String(node.object_real_id)
            } else {
                this.shareRelatedIdInt = Number(node.object_real_id)
                this.shareRelatedIdUuid = null
            }
            this.$nextTick(() => { this.$refs.shareDialog.open() })
        },

        openShareSubtree(node) {
            const items = []
            this.collectShareableNodes(node, items)
            if (!items.length) {
                alert('Este elemento no tiene hijos con objetos asociados para compartir.')
                return
            }
            this.shareSubtreeParentName = node.name
            this.shareSubtreeNodes = items
            this.$nextTick(() => { this.$refs.shareSubtreeDialog.open() })
        },

        collectShareableNodes(node, out) {
            (node.children || []).forEach(child => {
                if (child.obj_type && child.object_real_id) {
                    out.push({
                        object_type: child.obj_type,
                        related_id_int: this.isUuidType(child.obj_type) ? null : Number(child.object_real_id),
                        related_id_uuid: this.isUuidType(child.obj_type) ? String(child.object_real_id) : null,
                    })
                }
                this.collectShareableNodes(child, out)
            })
        },

        reindexSiblings(list) {
            list.forEach((n, i) => {
                if (n.seq !== i + 1) {
                    n.seq = i + 1
                    if (n.id > 0 && !n._created) n._updated = true
                }
            })
        },

        findParentId(nodes, id, parent_id = null) {
            for (const n of nodes) {
                if (n.id === id) return parent_id
                if (n.children) {
                    const found = this.findParentId(n.children, id, n.id)
                    if (found !== undefined) return found
                }
            }
            return undefined
        },

        async save() {
            this.saving = true
            try {
                const res = await this.api.fetch('/menu/saveMenuTree/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        tree: this.tree,
                        deleted: this.deletedNodes.map(n => n.id),
                        root_parent_id: this.rootId,
                    }),
                })
                const data = await res.json()
                if (!res.ok) throw new Error(data.detail)
                this.$emit('saved')
                await this.load()
            } catch (e) { console.error('Save error:', e); alert('Error al guardar: ' + e.message) }
            finally { this.saving = false }
        },

        addRootItem() { const n = this.newItem(); this.tree.push(n); this.startEdit(n) },

        addChild(node) {
            if (!node.children) node.children = []
            const n = this.newItem()
            n.seq = node.children.reduce((max, c) => Math.max(max, c.seq || 0), 0) + 1
            node.children.push(n)
            if (!this.expanded.includes(node.id)) this.expanded.push(node.id)
            this.startEdit(n)
        },

        newItem() {
            return { id: tempId--, name: 'Nuevo', url: null, icon: 'circle', seq: 999, children: [], obj_type: null, object_real_id: null, access_type: null, _created: true }
        },

        startEdit(node) {
            this.editingId = node.id
            this.editForm = node
            this.iconFilter = ''
        },

        confirmEdit(node) {
            Object.assign(node, {
                name: this.editForm.name,
                url: this.editForm.url || null,
                icon: this.editForm.icon || null,
                obj_type: this.editForm.obj_type ?? null,
                object_real_id: this.editForm.object_real_id || null,
                access_type: this.editForm.access_type || null,
            })
            if (!node._created) node._updated = true
            this.editingId = null
        },

        markDeleted(node) {
            const parentList = this.findSiblings(this.tree, node.id)
            if (node.id > 0) this.collectReal(node, this.deletedNodes)
            this.removeNode(this.tree, node.id)
            if (parentList) this.reindexSiblings(parentList)
        },

        collectReal(node, arr) {
            if (node.id > 0) arr.push({ id: node.id, name: node.name, icon: node.icon })
            node.children?.forEach(c => this.collectReal(c, arr))
        },

        restoreNode(node) {
            this.deletedNodes = this.deletedNodes.filter(d => d.id !== node.id)
            this.tree.push({ ...node, children: [], _updated: true })
        },

        removeNode(nodes, id) {
            const i = nodes.findIndex(n => n.id === id)
            if (i !== -1) { nodes.splice(i, 1); return true }
            return nodes.some(n => n.children && this.removeNode(n.children, id))
        },

        onDragStart(e, node) {
            this.dragged = node
            e.dataTransfer.effectAllowed = 'move'
            e.dataTransfer.setData('text/plain', String(node.id))
        },

        onDragOver(e, node) {
            if (!this.dragged || this.dragged.id === node.id || this.isDescendant(this.dragged, node.id)) return
            const rect = e.currentTarget.getBoundingClientRect()
            const pct = (e.clientY - rect.top) / rect.height
            this.dropPos = pct < 0.25 ? 'before' : pct > 0.75 ? 'after' : 'inside'
            this.dropTarget = node
        },

        onDragLeave(e) {
            if (!e.currentTarget.contains(e.relatedTarget)) { this.dropTarget = null; this.dropPos = null }
        },

        onDrop(e, node) {
            if (!this.dragged || this.dragged.id === node.id || this.isDescendant(this.dragged, node.id)) return
            const srcList = this.findSiblings(this.tree, this.dragged.id)
            this.removeNode(this.tree, this.dragged.id)
            let dstList
            if (this.dropPos === 'inside') {
                if (!node.children) node.children = []
                node.children.push(this.dragged)
                this.dragged.parent_id = node.id
                if (!this.expanded.includes(node.id)) this.expanded.push(node.id)
                dstList = node.children
            } else {
                dstList = this.findSiblings(this.tree, node.id)
                if (dstList) {
                    const idx = dstList.findIndex(n => n.id === node.id)
                    dstList.splice(this.dropPos === 'before' ? idx : idx + 1, 0, this.dragged)
                    this.dragged.parent_id = node.parent_id
                }
            }
            if (!this.dragged._created) this.dragged._updated = true
            if (srcList) this.reindexSiblings(srcList)
            if (dstList && dstList !== srcList) this.reindexSiblings(dstList)
            this.dragged = null; this.dropTarget = null; this.dropPos = null
        },

        findSiblings(nodes, id) {
            if (nodes.some(n => n.id === id)) return nodes
            for (const n of nodes) {
                if (n.children) { const f = this.findSiblings(n.children, id); if (f) return f }
            }
            return null
        },

        isDescendant(node, targetId) {
            if (!node.children) return false
            return node.children.some(c => c.id === targetId || this.isDescendant(c, targetId))
        },

        nodeClass(n) {
            if (n._created) return 'bg-green-1 node-border-green'
            if (n._updated) return 'bg-blue-1 node-border-blue'
            return ''
        },
    },
}
</script>

<style scoped>
.menu-editor { margin: 0 auto; }
.menu-node { border-radius: 6px; padding: 2px 6px; position: relative; transition: background .15s; }
.menu-node:hover { background: rgba(0,0,0,.04); }
.node-border-green { border-left: 3px solid var(--q-positive); }
.node-border-blue { border-left: 3px solid var(--q-info); }
.drop-bar { position: absolute; left: 0; right: 0; height: 3px; background: var(--q-primary); border-radius: 2px; pointer-events: none; z-index: 5; }
.drop-bar--before { top: 0; }
.drop-bar--after { bottom: 0; }
.drop-bar--inside { height: auto; top: 0; bottom: 0; background: transparent; border: 2px dashed var(--q-primary); border-radius: 6px; }
</style>